import React, { useEffect, useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TablePagination,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { axiosGetCall, axiosPostCall } from 'src/services/apisCall';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { styled } from '@mui/material/styles';
import AddLogger from './addLogger';
import EditLogger from './editLogger';
import { renderErrorMessage, renderSuccessMessage } from 'src/lib/toastMessage';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';

const columns = [
  { id: 'env', label: 'Env', align: 'start' },
  { id: 'level', label: 'Level', align: 'center' },
  { id: 'time', label: 'Time', align: 'center' },
  { id: 'scriptName', label: 'Script Name', align: 'center' },
  { id: 'Edit', label: 'Edit', align: 'center' },
];

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return isNaN(date) ? '-' : date.toLocaleDateString();
};

const Logger = () => {
  const [selectOptions, setSelectOptions] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [addOpenModal, setAddOpenModal] = useState(false);
  const [editOpenModal, setEditOpenModal] = useState(false);
  const [editData, setEditData] = useState('');
  const [loadit, forceLoad] = useState(false);
  const [addLoad, setAddLoad] = useState(false);

  const [searchValue, setSearchValue] = useState({ env: '' });

  const handleChange = (event) => {
    setSearchValue(() => ({ env: event.target.value }));
  };
  const fetchLogData = async () => {
    setLoading(true);
    try {
      const data = await axiosGetCall('fetchLogLevel', searchValue);
      const envList = await axiosGetCall('fetchLoggerEnv');
      setSelectOptions(envList);

      const formattedRows = data?.map((doc, index) => ({
        id: index,
        level: parseInt(doc?.level),
        time: formatDate(doc.time) || '',
        env: doc.env || '-',
        scriptName: doc.scriptName || '-',
      }));

      setRows(formattedRows);
      setTotalCount(formattedRows.length);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogData();
  }, [loadit, addLoad, searchValue.env]);

  const handleLevelChange = async (row, newLevel) => {
    setRows((prevRows) => prevRows?.map((r) => (r.id === row.id ? { ...r, level: newLevel } : r)));
    const { env, scriptName } = row;
    const updatedData = {
      env,
      scriptName,
      logLevel: newLevel,
    };
    try {
      const response = await axiosPostCall('changeLogLevel', updatedData);
      if (response) {
        renderSuccessMessage('Changed level successfully');
      }
    } catch (error) {
      renderErrorMessage(error?.message);
    }
  };

  const handleEdit = (rowData) => {
    setEditData(rowData);
    setEditOpenModal(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const TableFilterSection = styled('div')(({ theme }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: '#fff',
  }));

  const AddButton = () => (
    <Button
      startIcon={<PersonAddIcon />}
      sx={{
        height: 35,
        backgroundColor: 'rgb(33, 43, 54)',
        color: '#fff',
        px: 2,
        '&:hover': {
          backgroundColor: 'rgb(69, 79, 91)',
        },
      }}
      onClick={() => setAddOpenModal(true)}
    >
      Add Logger
    </Button>
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
        {/* {loading ? (
          // <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
          //   <CircularProgress />
          // </Box>
        ) : ( */}
        <>
          <TableContainer component={Paper} sx={{ maxHeight: '72vh' }}>
            <TableFilterSection>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <FormControl
                  size="small"
                  sx={{
                    minWidth: 180,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                  }}
                >
                  <InputLabel id="dropdown-label">Select Env</InputLabel>
                  <Select
                    labelId="dropdown-label"
                    id="mui-dropdown"
                    value={searchValue.env || ''}
                    label="Select Env"
                    onChange={handleChange}
                  >
                    {selectOptions?.map((option, index) => (
                      <MenuItem key={index} value={option.value || option}>
                        {option.label || option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {searchValue.env && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      setSearchValue({ env: '' });
                    }}
                  >
                    Clear Filter
                  </Button>
                )}
              </Box>

              {AddButton()}
            </TableFilterSection>

            <Table size="small">
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      sx={{
                        position: 'sticky',
                        top: 64,
                        backgroundColor: '#f4f6f8',
                        zIndex: 1,
                        fontWeight: 'bold',
                        py: 1,
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      No data found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow key={row.id}>
                      {columns.map((column) => (
                        <TableCell key={column.id} align={column.align}>
                          {column.id === 'level' ? (
                            <ToggleButtonGroup
                              value={row.level}
                              exclusive
                              onChange={(e, newValue) => newValue !== null && handleLevelChange(row, newValue)}
                              size="small"
                              sx={{
                                border: '1px solid',
                                borderColor: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '8px',
                                height: '40px',
                              }}
                            >
                              <ToggleButton
                                value={20}
                                sx={{
                                  padding: '3px 8px',
                                  fontSize: '12px',
                                  height: '30px',
                                  '&.Mui-selected': {
                                    backgroundColor: '#d7d7d8',
                                  },
                                }}
                              >
                                Info
                              </ToggleButton>

                              <ToggleButton
                                value={30}
                                sx={{
                                  padding: '3px 8px',
                                  fontSize: '12px',
                                  height: '30px',
                                  '&.Mui-selected': {
                                    backgroundColor: '#d7d7d8',
                                  },
                                }}
                              >
                                Debug
                              </ToggleButton>
                            </ToggleButtonGroup>
                          ) : column.id === 'Edit' ? (
                            <Button variant="outlined" size="small" onClick={() => handleEdit(row)}>
                              Edit
                            </Button>
                          ) : (
                            row[column.id]
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ p: 1, borderRadius: '4px', backgroundColor: '#fff' }}>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </>
        {/* )} */}
        <EditLogger
          open={editOpenModal}
          onClose={() => setEditOpenModal(false)}
          data={editData}
          forceLoad={forceLoad}
          loadit={loadit}
        />
        <AddLogger
          open={addOpenModal}
          onClose={() => setAddOpenModal(false)}
          setAddLoad={setAddLoad}
          addLoad={addLoad}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default Logger;
